import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import RadioGroup from "../components/RadioGroup";
import FormSection from "../components/FormSection";
import FormSelect from "../components/FormSelect";
import FormInput from "../components/FormInput";
import backArrow from "../images/Layer 51.svg";

const MedicalLegalInfo = ({
  primaryColor = "#1F3047",
  secondaryColor = "#349AC8",
  textColor = "#181818",
  placeholderColor = "#717171",
  onBack = () => {},
  onSubmit = () => {},
  data
}) => {
  // Form state
  const [formData, setFormData] = useState({
    staffFirstName: data?.medicalLegal?.staffFirstName || "",
    staffLastName: data?.medicalLegal?.staffLastName || "",
    staffEmail: data?.medicalLegal?.staffEmail || "",
    staffPhone: data?.medicalLegal?.staffPhone || "",
    staffPosition: data?.medicalLegal?.staffPosition || "",
    staffPositionOther: data?.medicalLegal?.staffPositionOther || "",
    staffEmployeeId: data?.medicalLegal?.staffEmployeeId || "",
    bloodPressure: data?.medicalLegal?.bloodPressure || "",
    temperature: data?.medicalLegal?.temperature || "",
    pulse: data?.medicalLegal?.pulse || "",
    oxygenLevel: data?.medicalLegal?.oxygenLevel || "",
    bloodAlcohol: data?.medicalLegal?.bloodAlcohol || "",
    urinalysis: data?.medicalLegal?.urinalysis || "",
    requestNumber: data?.medicalLegal?.requestNumber || "",
    mentalHealthCondition: data?.medicalLegal?.mentalHealthCondition ?? null,
    mentalHealthDetails: data?.medicalLegal?.mentalHealthDetails || "",
    medications: data?.medicalLegal?.medications ?? null,
    medicationCompliance: data?.medicalLegal?.medicationCompliance || "",
    physicalLimitations: data?.medicalLegal?.physicalLimitations ?? null,
    physicalLimitationsDetails: data?.medicalLegal?.physicalLimitationsDetails || "",
    probationStatus: data?.medicalLegal?.probationStatus ?? null,
    disabilityBenefits: data?.medicalLegal?.disabilityBenefits ?? null,
    legalCases: data?.medicalLegal?.legalCases ?? null,
    legalCasesDetails: data?.medicalLegal?.legalCasesDetails || "",
    sobrietyDate: data?.medicalLegal?.sobrietyDate || "",
    attendedRehab: data?.medicalLegal?.attendedRehab ?? null,
    rehabTimes: data?.medicalLegal?.rehabTimes || "",
    recentRehabCenter: data?.medicalLegal?.recentRehabCenter || "",
    reasonForLeaving: data?.medicalLegal?.reasonForLeaving || ""
  });
  

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isSubmittable, setIsSubmittable] = useState(false);

  const mandatoryFields = [
    "staffFirstName",
    "staffLastName",
    "staffPosition",
    "bloodPressure",
    "temperature",
    "pulse",
    "oxygenLevel",
  ];

  const validationRules = {
    staffFirstName: { required: true, message: "First name is required" },
    staffLastName: { required: true, message: "Last name is required" },
    staffEmail: {
      required: true,
      pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
      message: "Valid email is required",
    },
    staffPhone: {
      required: true,
      pattern: /^\d{10}$/,
      message: "Phone must be 10 digits only",
    },
    staffPosition: { required: true, message: "Position is required" },
    staffEmployeeId: { required: true, message: "Employee ID is required" },
    staffPositionOther: {
      required: formData.staffPosition === "Other",
      message: "Please specify position",
    },
    bloodPressure: {
      required: true,
      pattern: /^\d{1,3}\/\d{1,3}$/,
      message: "Enter valid BP (e.g. 120/80)",
    },
    temperature: {
      required: true,
      pattern: /^\d{2,3}(\.\d)?$/,
      message: "Enter valid temperature (e.g. 98.6)",
    },
    pulse: {
      required: true,
      pattern: /^\d{2,3}$/,
      message: "Enter valid pulse (e.g. 72)",
    },
    oxygenLevel: {
      required: true,
      pattern: /^\d{2,3}%?$/,
      message: "Enter valid oxygen level (e.g. 97%)",
    },
  };

  useEffect(() => {
    const newErrors = {};
    let hasErrors = false;

    mandatoryFields.forEach((field) => {
      const error = validateField(field, formData[field]);
      // if (error) {
      //   newErrors[field] = error;
      //   hasErrors = true;
      // }
    });

    setErrors(newErrors);
    setIsSubmittable(!hasErrors);
  }, [formData]);

  const validateField = (name, value) => {
    const rule = validationRules[name];
    if (!rule) return "";

    if (rule.required && !value) return rule.message;
    if (rule.pattern && value && !rule.pattern.test(value)) return rule.message;

    return "";
  };

  const validateForm = () => {
    const newErrors = {};
    let isValid = true;

    mandatoryFields.forEach((field) => {
      const error = validateField(field, formData[field]);
      if (error) {
        newErrors[field] = error;
        isValid = false;
      }
    });

    if (formData.staffPosition === "Other") {
      const error = validateField(
        "staffPositionOther",
        formData.staffPositionOther
      );
      if (error) {
        newErrors.staffPositionOther = error;
        isValid = false;
      }
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (errors[name]) {
      const error = validateField(name, value);
      setErrors((prev) => ({ ...prev, [name]: error }));
    }
  };

  const handleRadioChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));

    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    const error = validateField(name, value);
    setErrors((prev) => ({ ...prev, [name]: error }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const isValid = validateForm();

    if (isValid) {
      onSubmit(formData);
    } else {
      const newTouched = {};
      mandatoryFields.forEach((field) => {
        newTouched[field] = true;
      });
      setTouched(newTouched);

      const firstErrorField = Object.keys(errors)[0];
      if (firstErrorField) {
        document.querySelector(`[name="${firstErrorField}"]`)?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }
    }
  };

  const getInputProps = (name) => ({
    name,
    value: formData[name] || "",
    onChange: handleChange,
    onBlur: handleBlur,
    error: touched[name] ? errors[name] : "",
    color: secondaryColor,
    textColor,
    placeholderColor,
  });

  return (
    <form onSubmit={handleSubmit} style={{ width: "100%" }}>
      <div
        style={{
          width: "100%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-start",
          alignItems: "center",
          gap: "48px",
          padding: "20px",
          maxWidth: "1200px",
          margin: "0 auto",
        }}
      >
        <div
          style={{
            alignSelf: "stretch",
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-start",
            alignItems: "center",
            gap: "16px",
          }}
        >
          <h1
            style={{
              textAlign: "center",
              color: primaryColor,
              fontSize: "32px",
              fontFamily: "Mauren, sans-serif",
              fontWeight: "100",
              margin: 0,
            }}
          >
            Medical & Legal Information
          </h1>
          <div style={{ textAlign: "center" }}>
            <span
              style={{
                color: primaryColor,
                fontSize: "16px",
                fontFamily: "'FONTSPRING DEMO - Commuters Sans', sans-serif",
                fontWeight: "400",
                lineHeight: "24px",
              }}
            >
              This section is intended for completion by authorized Legacy
              Healing{" "}
            </span>
            <span
              style={{
                color: secondaryColor,
                fontSize: "16px",
                fontFamily: "'FONTSPRING DEMO - Commuters Sans', sans-serif",
                fontWeight: "700",
                lineHeight: "24px",
              }}
            >
              staff only
            </span>
            <span
              style={{
                color: primaryColor,
                fontSize: "16px",
                fontFamily: "'FONTSPRING DEMO - Commuters Sans', sans-serif",
                fontWeight: "400",
                lineHeight: "24px",
              }}
            >
              .
            </span>
          </div>
        </div>

        <div
          style={{
            alignSelf: "stretch",
            display: "flex",
            flexDirection: "column",
            gap: "32px",
          }}
        >
          <FormSection
            title="Staff Details"
            primaryColor={primaryColor}
            secondaryColor={secondaryColor}
          >
            <div style={formRowStyle}>
              <FormInput
                label="Staff First Name*"
                {...getInputProps("staffFirstName")}
                placeholder="Enter first name"
              />
              <FormInput
                label="Staff Last Name*"
                {...getInputProps("staffLastName")}
                placeholder="Enter last name"
              />
            </div>

            <div style={formRowStyle}>
              <FormSelect
                label="Role*"
                {...getInputProps("staffPosition")}
                options={[
                  "Counselor",
                  "Nurse",
                  "Doctor",
                  "Case Manager",
                  "Administrator",
                  "Support Staff",
                  "BHT",
                  "Other",
                ]}
                placeholder="Select role"
              />
              {formData.staffPosition === "Other" && (
                <FormInput
                  label="Specify Position*"
                  {...getInputProps("staffPositionOther")}
                  placeholder="Enter position"
                  width="450px"
                />
              )}
            </div>
          </FormSection>

          <FormSection
            title="Health Vitals"
            primaryColor={primaryColor}
            secondaryColor={secondaryColor}
          >
            <div style={formRowStyle}>
              <FormInput
                label="Blood Pressure*"
                {...getInputProps("bloodPressure")}
                placeholder="Ex: 120/80"
              />
              <FormInput
                label="Temperature (°F)*"
                {...getInputProps("temperature")}
                placeholder="Ex: 98.6"
              />
            </div>
            <div style={formRowStyle}>
              <FormInput
                label="Pulse (BPM)*"
                {...getInputProps("pulse")}
                placeholder="Ex: 72"
              />
              <FormInput
                label="Oxygen Level (%)*"
                {...getInputProps("oxygenLevel")}
                placeholder="Ex: 97%"
              />
            </div>
            <div style={formRowStyle}>
              <FormInput
                label="Blood Alcohol (BA)"
                {...getInputProps("bloodAlcohol")}
                placeholder="Ex: 0.08"
              />
              <FormInput
                label="Urinalysis (UA)"
                {...getInputProps("urinalysis")}
                placeholder="Ex: 1.08"
              />
            </div>
            <div style={formRowStyle}>
              <FormInput
                label="Request # (REQ)"
                {...getInputProps("requestNumber")}
                placeholder="Ex: #20492"
              />
            </div>
          </FormSection>

          <FormSection
            title="Medical History & Support"
            primaryColor={primaryColor}
            secondaryColor={secondaryColor}
          >
            <div style={formRowStyle}>
              <RadioGroup
                label="Do you have a diagnosed mental health condition?"
                name="mentalHealthCondition"
                value={formData.mentalHealthCondition}
                options={["Yes", "No"]}
                onChange={(value) =>
                  handleRadioChange("mentalHealthCondition", value === "Yes")
                }
                color={primaryColor}
                textColor={textColor}
              />
              {formData.mentalHealthCondition && (
                <FormInput
                  label="If yes, please specify"
                  name="mentalHealthDetails"
                  value={formData.mentalHealthDetails}
                  onChange={handleChange}
                  placeholder="Ex: Depression, PTSD, Anxiety"
                  color={secondaryColor}
                  textColor={textColor}
                  placeholderColor={placeholderColor}
                  width="450px"
                />
              )}
            </div>
            <div style={formRowStyle}>
              <RadioGroup
                label="Are you currently on any medications?"
                name="medications"
                value={formData.medications}
                options={["Yes", "No"]}
                onChange={(value) =>
                  handleRadioChange("medications", value === "Yes")
                }
                color={primaryColor}
                textColor={textColor}
              />
              {formData.medications && (
                <FormSelect
                  label="Medication compliance"
                  name="medicationCompliance"
                  value={formData.medicationCompliance}
                  onChange={handleChange}
                  placeholder="Select compliance status"
                  color={secondaryColor}
                  textColor={textColor}
                  placeholderColor={placeholderColor}
                  width="450px"
                  options={[
                    "Compliant",
                    "Partially Compliant",
                    "Non-Compliant",
                    "Not Applicable",
                  ]}
                />
              )}
            </div>
            <div style={formRowStyle}>
              <RadioGroup
                label="Do you have physical limitations or disabilities?"
                name="physicalLimitations"
                value={formData.physicalLimitations}
                options={["Yes", "No"]}
                onChange={(value) =>
                  handleRadioChange("physicalLimitations", value === "Yes")
                }
                color={primaryColor}
                textColor={textColor}
              />
              {formData.physicalLimitations && (
                <FormInput
                  label="If yes, please specify"
                  name="physicalLimitationsDetails"
                  value={formData.physicalLimitationsDetails}
                  onChange={handleChange}
                  placeholder="Ex: Mobility issues, hearing impairment"
                  color={secondaryColor}
                  textColor={textColor}
                  placeholderColor={placeholderColor}
                  width="450px"
                />
              )}
            </div>
          </FormSection>

          <FormSection
            title="Legal & Probation Status"
            primaryColor={primaryColor}
            secondaryColor={secondaryColor}
          >
            <div style={formRowStyle}>
              <RadioGroup
                label="Are you currently on probation or house arrest?"
                name="probationStatus"
                value={formData.probationStatus}
                options={["Yes", "No"]}
                onChange={(value) =>
                  handleRadioChange("probationStatus", value === "Yes")
                }
                color={primaryColor}
                textColor={textColor}
              />
              <RadioGroup
                label="Are you currently on disability benefits?"
                name="disabilityBenefits"
                value={formData.disabilityBenefits}
                options={["Yes", "No"]}
                onChange={(value) =>
                  handleRadioChange("disabilityBenefits", value === "Yes")
                }
                color={primaryColor}
                textColor={textColor}
              />
              <RadioGroup
                label="Are you involved in any active legal cases?"
                name="legalCases"
                value={formData.legalCases}
                options={["Yes", "No"]}
                onChange={(value) =>
                  handleRadioChange("legalCases", value === "Yes")
                }
                color={primaryColor}
                textColor={textColor}
              />
            </div>
            {formData.legalCases && (
              <FormInput
                label="If yes, please specify"
                name="legalCasesDetails"
                value={formData.legalCasesDetails}
                onChange={handleChange}
                placeholder="Ex: Court appearances, pending charges"
                color={secondaryColor}
                textColor={textColor}
                placeholderColor={placeholderColor}
                height="140px"
                multiline
              />
            )}
          </FormSection>

          <FormSection
            title="Sobriety & Recovery History"
            primaryColor={primaryColor}
            secondaryColor={secondaryColor}
          >
            <div style={formRowStyle}>
              <FormInput
                label="Sobriety Date"
                name="sobrietyDate"
                value={formData.sobrietyDate}
                onChange={handleChange}
                placeholder="MM/DD/YYYY"
                color={secondaryColor}
                textColor={textColor}
                placeholderColor={placeholderColor}
              />
            </div>
            <div style={formRowStyle}>
              <RadioGroup
                label="Have you attended rehab before?"
                name="attendedRehab"
                value={formData.attendedRehab}
                options={["Yes", "No"]}
                onChange={(value) =>
                  handleRadioChange("attendedRehab", value === "Yes")
                }
                color={primaryColor}
                textColor={textColor}
              />
              {formData.attendedRehab && (
                <FormInput
                  label="If yes, how many times?"
                  name="rehabTimes"
                  value={formData.rehabTimes}
                  onChange={handleChange}
                  placeholder="Ex: 2"
                  color={secondaryColor}
                  textColor={textColor}
                  placeholderColor={placeholderColor}
                />
              )}
            </div>
            {formData.attendedRehab && (
              <div style={formRowStyle}>
                <FormInput
                  label="Most recent rehab center"
                  name="recentRehabCenter"
                  value={formData.recentRehabCenter}
                  onChange={handleChange}
                  placeholder="Ex: Hope Springs Recovery, FL"
                  color={secondaryColor}
                  textColor={textColor}
                  placeholderColor={placeholderColor}
                />
                <FormInput
                  label="Reason for leaving"
                  name="reasonForLeaving"
                  value={formData.reasonForLeaving}
                  onChange={handleChange}
                  placeholder="Ex: Completed program, insurance issues"
                  color={secondaryColor}
                  textColor={textColor}
                  placeholderColor={placeholderColor}
                />
              </div>
            )}
          </FormSection>
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: "24px",
            width: "100%",
            marginTop: "20px",
          }}
        >
          <button
            type="button"
            onClick={onBack}
            style={{
              padding: "12px 16px",
              borderRadius: "16px",
              outline: `1px solid ${secondaryColor}`,
              border: "none",
              background: "transparent",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              cursor: "pointer",
            }}
          >
            <div
              style={{
                width: "24px",
                height: "24px",
                padding: "6px",
                background: secondaryColor,
                borderRadius: "50%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <img src={backArrow} alt="" />
            </div>
            <span
              style={{
                color: primaryColor,
                fontSize: "16px",
                fontFamily: "'FONTSPRING DEMO - Commuters Sans', sans-serif",
                fontWeight: "400",
              }}
            >
              Back to Residency Details
            </span>
          </button>

          <button
            type="submit"
            style={{
              padding: "12px 16px",
              background: isSubmittable ? secondaryColor : "#cccccc",
              borderRadius: "16px",
              border: "none",
              color: "white",
              fontSize: "16px",
              fontFamily: "'FONTSPRING DEMO - Commuters Sans', sans-serif",
              fontWeight: "400",
              cursor: isSubmittable ? "pointer" : "not-allowed",
            }}
            disabled={!isSubmittable}
          >
            Continue to Belongings & Devices
          </button>
        </div>
      </div>
    </form>
  );
};

const formRowStyle = {
  display: "flex",
  gap: "24px",
  width: "100%",
};

MedicalLegalInfo.propTypes = {
  primaryColor: PropTypes.string,
  secondaryColor: PropTypes.string,
  textColor: PropTypes.string,
  placeholderColor: PropTypes.string,
  onBack: PropTypes.func,
  onSubmit: PropTypes.func,
};

export default MedicalLegalInfo;
